/**
 * Bloc cadeaux par palier (panier / tiroir) : retrait, choix de variante, « Réajouter »,
 * synchronisation des lignes cadeau. data-action "cart-threshold-gift-*" = action dans le panier.
 */
import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

const READD_ACTIVE = 'cart-threshold-gifts__readd--active';

/** localStorage : survit au changement de page / onglet (même origine boutique). */
const DISMISSED_THRESHOLD_GIFTS_STORAGE_KEY = 'cart-threshold-gift-dismissed';

const userRemovedThresholdGiftByProductId = new Set();
/** Variante du cadeau « une seule variante » : bloque l’ajout auto même sans nœud DOM (tiroir fermé). */
const userRemovedThresholdGiftByVariantId = new Set();

function getDismissedStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function persistDismissedThresholdGifts() {
  const storage = getDismissedStorage();
  if (!storage) {
    return;
  }
  try {
    const payload = {
      p: Array.from(userRemovedThresholdGiftByProductId),
      v: Array.from(userRemovedThresholdGiftByVariantId),
    };
    if (payload.p.length === 0 && payload.v.length === 0) {
      storage.removeItem(DISMISSED_THRESHOLD_GIFTS_STORAGE_KEY);
      return;
    }
    storage.setItem(DISMISSED_THRESHOLD_GIFTS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota, navigation privée */
  }
}

function loadDismissedThresholdGiftsFromStorage() {
  const storage = getDismissedStorage();
  if (!storage) {
    return;
  }
  try {
    const raw = storage.getItem(DISMISSED_THRESHOLD_GIFTS_STORAGE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed.p)) {
      for (const id of parsed.p) {
        if (id != null && String(id) !== '') {
          userRemovedThresholdGiftByProductId.add(String(id));
        }
      }
    }
    if (Array.isArray(parsed.v)) {
      for (const id of parsed.v) {
        const n = Number(id);
        if (!Number.isNaN(n) && n > 0) {
          userRemovedThresholdGiftByVariantId.add(String(n));
        }
      }
    }
  } catch {
    /* JSON invalide */
  }
}

/** Réaligne la mémoire sur le disque avant sync / affichage (navigation, Turbo, etc.). */
function hydrateDismissedThresholdGiftsFromPersistence() {
  userRemovedThresholdGiftByProductId.clear();
  userRemovedThresholdGiftByVariantId.clear();
  loadDismissedThresholdGiftsFromStorage();
}

function markUserRemovedThresholdGift(productId) {
  if (productId) {
    userRemovedThresholdGiftByProductId.add(String(productId));
    persistDismissedThresholdGifts();
  }
}

function markUserRemovedThresholdGiftVariant(variantId) {
  const n = Number(variantId);
  if (!Number.isNaN(n) && n > 0) {
    userRemovedThresholdGiftByVariantId.add(String(n));
    persistDismissedThresholdGifts();
  }
}

function clearDismissedThresholdGiftPair(productId, variantId) {
  if (productId) {
    userRemovedThresholdGiftByProductId.delete(String(productId));
  }
  const n = Number(variantId);
  if (!Number.isNaN(n) && n > 0) {
    userRemovedThresholdGiftByVariantId.delete(String(n));
  }
  persistDismissedThresholdGifts();
}

function isUserRemovedThresholdGift(productId) {
  if (!productId) {
    return false;
  }
  return userRemovedThresholdGiftByProductId.has(String(productId));
}

function getThresholdProductIdFromRemoveControl(control) {
  const id = control.closest('article')?.querySelector('[data-threshold-gift-product-id]')?.getAttribute('data-threshold-gift-product-id');
  return id || null;
}

function getThresholdAutoVariantIdFromRemoveControl(control) {
  const auto = control.closest('article')?.querySelector('[data-auto-add-threshold-gift][data-variant-id]');
  const v = auto?.getAttribute('data-variant-id');
  if (v == null || String(v) === '') {
    return null;
  }
  const n = Number(v);
  if (Number.isNaN(n) || n <= 0) {
    return null;
  }
  return n;
}

function isVariantAutoAddSuppressedByUser(variantId) {
  const n = Number(variantId);
  if (!Number.isNaN(n) && n > 0 && userRemovedThresholdGiftByVariantId.has(String(n))) {
    return true;
  }
  const el = document.querySelector(
    `[data-auto-add-threshold-gift][data-variant-id="${Number(variantId)}"]`,
  );
  if (!el) {
    return false;
  }
  const productId = el.closest('article')?.querySelector('[data-threshold-gift-product-id]')?.getAttribute('data-threshold-gift-product-id');
  return isUserRemovedThresholdGift(productId);
}

/**
 * /cart.js peut exposer les line properties en objet ou (selon thème / appli) autrement.
 */
function getLineItemPropertyValue(item, key) {
  const raw = item?.properties;
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (key in raw) {
      return raw[key];
    }
  }
  if (Array.isArray(raw)) {
    for (const row of raw) {
      if (row == null) {
        continue;
      }
      if (row.name === key) {
        return row.value;
      }
      if (Array.isArray(row) && row[0] === key) {
        return row[1] ?? null;
      }
    }
  }
  return null;
}

/**
 * Retrait d'une ligne cadeau (mini-carte) : même API que le panier classique.
 * Panier thème (cart-drawer) : re-render section. Page panier : rechargement.
 */
function init() {
  if (window.__cartThresholdGiftRemoveBound) {
    return;
  }
  window.__cartThresholdGiftRemoveBound = true;
  window.__cartThresholdGiftSyncPending = false;

  document.addEventListener('click', (event) => {
    const readd = event.target.closest('[data-action="cart-threshold-gift-readd"]');
    if (readd) {
      event.preventDefault();
      const variantId = Number(readd.getAttribute('data-variant-id'));
      const productId = readd.getAttribute('data-product-id');
      if (Number.isNaN(variantId) || variantId <= 0) {
        return;
      }
      const cartDrawer = readd.closest('cart-drawer');
      readd.setAttribute('disabled', 'disabled');
      clearDismissedThresholdGiftPair(productId, variantId);
      addThresholdGiftLineItems([variantId], cartDrawer)
        .then((parsed) => {
          readd.removeAttribute('disabled');
          if (parsed?.errors) {
            if (productId) {
              markUserRemovedThresholdGift(productId);
            }
            markUserRemovedThresholdGiftVariant(variantId);
            void revealReaddButtons();
            document.dispatchEvent(
              new CustomEvent('toast:open', { detail: { type: 'error', message: parsed.errors.join?.(', ') || 'Error' } }),
            );
            return;
          }
          if (cartDrawer) {
            const cartIcon = document.querySelector('cart-icon');
            const currentItemCount = cartIcon ? cartIcon.getCurrentItemCount() : 0;
            const itemCount = parsed?.item_count ?? currentItemCount + 1;
            document.dispatchEvent(
              new CartUpdatedEvent(
                {},
                { itemCount, source: 'cart-threshold-gift', sections: parsed?.sections || {} },
              ),
            );
            return;
          }
          window.location.reload();
        })
        .catch((err) => {
          readd.removeAttribute('disabled');
          if (productId) {
            markUserRemovedThresholdGift(productId);
          }
          markUserRemovedThresholdGiftVariant(variantId);
          void revealReaddButtons();
          console.error(err);
        });
      return;
    }

    const control = event.target.closest('[data-action="cart-threshold-gift-remove"]');
    if (!control) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const line = control.getAttribute('data-line');
    if (!line) {
      return;
    }
    const removedProductId = getThresholdProductIdFromRemoveControl(control);
    const removedVariantId = getThresholdAutoVariantIdFromRemoveControl(control);
    if (removedProductId) {
      markUserRemovedThresholdGift(removedProductId);
    }
    if (removedVariantId != null) {
      markUserRemovedThresholdGiftVariant(removedVariantId);
    }
    const cartDrawer = control.closest('cart-drawer');
    if (cartDrawer) {
      const body = {
        line,
        quantity: 0,
        sections: cartDrawer.dataset.sectionId,
        sections_url: window.location.pathname,
      };
      control.classList.add('is-loading');
      fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.text())
        .then((t) => JSON.parse(t))
        .then((parsed) => {
          if (parsed.errors) {
            clearDismissedThresholdGiftPair(removedProductId, removedVariantId);
            document.dispatchEvent(
              new CustomEvent('toast:open', { detail: { type: 'error', message: parsed.errors.join?.(', ') || 'Error' } }),
            );
            return;
          }
          document.dispatchEvent(
            new CartUpdatedEvent(
              {},
              { itemCount: parsed.item_count, source: 'cart-threshold-gift', sections: parsed.sections },
            ),
          );
        })
        .catch((err) => {
          clearDismissedThresholdGiftPair(removedProductId, removedVariantId);
          console.error(err);
        })
        .finally(() => {
          control.classList.remove('is-loading');
        });
    } else {
      control.classList.add('is-loading');
      fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: 0 }),
      })
        .then((r) => r.text())
        .then((t) => JSON.parse(t))
        .then((parsed) => {
          if (parsed.errors) {
            clearDismissedThresholdGiftPair(removedProductId, removedVariantId);
            document.dispatchEvent(
              new CustomEvent('toast:open', { detail: { type: 'error', message: parsed.errors.join?.(', ') || 'Error' } }),
            );
            return;
          }
          window.location.reload();
        })
        .catch((err) => {
          clearDismissedThresholdGiftPair(removedProductId, removedVariantId);
          console.error(err);
        });
    }
  });

  document.addEventListener('click', (event) => {
    const toggleButton = event.target.closest('[data-action="toggle-threshold-gift-variants"]');
    if (!toggleButton) {
      return;
    }

    event.preventDefault();

    const targetId = toggleButton.getAttribute('data-target');
    if (!targetId) {
      return;
    }

    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }

    const expanded = toggleButton.getAttribute('aria-expanded') === 'true';
    toggleButton.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    target.classList.toggle('hidden', expanded);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-ref="threshold-gift-variant-select"]');
    if (!select) {
      return;
    }

    const variantId = Number(select.value);

    if (Number.isNaN(variantId) || variantId <= 0) {
      return;
    }

    const cartDrawer = select.closest('cart-drawer');
    select.setAttribute('disabled', 'disabled');

    addThresholdGiftLineItems([variantId], cartDrawer)
      .then((parsed) => {
        if (parsed?.errors) {
          document.dispatchEvent(
            new CustomEvent('toast:open', { detail: { type: 'error', message: parsed.errors.join?.(', ') || 'Error' } }),
          );
          return;
        }

        if (cartDrawer) {
          const cartIcon = document.querySelector('cart-icon');
          const currentItemCount = cartIcon ? cartIcon.getCurrentItemCount() : 0;
          const itemCount = parsed?.item_count ?? currentItemCount + 1;

          document.dispatchEvent(
            new CartUpdatedEvent(
              {},
              {
                itemCount,
                source: 'cart-threshold-gift',
                sections: parsed?.sections || {},
              },
            ),
          );
          return;
        }

        window.location.reload();
      })
      .catch(console.error)
      .finally(() => {
      select.removeAttribute('disabled');
      });
  });

  const scheduleThresholdGiftSync = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void revealReaddButtons().then(() => synchronizeThresholdGifts());
      });
    });
  };

  scheduleThresholdGiftSync();
  document.addEventListener(ThemeEvents.cartUpdated, () => {
    scheduleThresholdGiftSync();
  });
  document.addEventListener(ThemeEvents.cartDrawerDidOpen, () => {
    scheduleThresholdGiftSync();
  });
}

function getEligibleThresholdGiftVariantIds() {
  const targets = document.querySelectorAll('[data-auto-add-threshold-gift][data-variant-id]');
  const variantIds = new Set();

  for (const target of targets) {
    const value = Number(target.getAttribute('data-variant-id'));
    if (!Number.isNaN(value) && value > 0) {
      variantIds.add(value);
    }
  }

  return variantIds;
}

function getEligibleThresholdGiftProductIds() {
  const targets = document.querySelectorAll('[data-threshold-gift-product-id]');
  const productIds = new Set();

  for (const target of targets) {
    const value = Number(target.getAttribute('data-threshold-gift-product-id'));
    if (!Number.isNaN(value) && value > 0) {
      productIds.add(value);
    }
  }

  return productIds;
}

function isThresholdGiftLineItem(item) {
  if (!item) {
    return false;
  }
  const t = getLineItemPropertyValue(item, '_threshold_gift');
  const a = getLineItemPropertyValue(item, '_attribution');
  if (a === 'Rebuy Tiered Progress Bar') {
    return true;
  }
  if (t === 'true' || t === true) {
    return true;
  }
  return false;
}

/**
 * Ligne panier = cadeau configuré (id produit thème, variante cadeau optionnelle)
 */
function lineMatchesThresholdGiftInCart(line, productId, giftVariantIdAttr) {
  if (!line) {
    return false;
  }
  const pid = line.product_id ?? line.productId;
  const vid = line.variant_id ?? line.variantId;
  if (productId != null && String(pid) === String(productId)) {
    return true;
  }
  if (giftVariantIdAttr != null && String(giftVariantIdAttr) !== '' && String(vid) === String(giftVariantIdAttr)) {
    return true;
  }
  return false;
}

function isThresholdGiftInCart(cart, productId, giftVariantIdAttr) {
  if (!cart?.items?.length) {
    return false;
  }
  return cart.items.some((line) => lineMatchesThresholdGiftInCart(line, productId, giftVariantIdAttr));
}

/**
 * Cadeau déjà en ligne = retirer l’intention « j’ai supprimé l’offert »
 */
function clearRemovedIfGiftLinePresentInDom(cart) {
  if (!cart?.items?.length) {
    return;
  }
  for (const el of document.querySelectorAll('[data-threshold-gift-product-id]')) {
    const productId = el.getAttribute('data-threshold-gift-product-id');
    if (!productId) {
      continue;
    }
    const card = el.closest('article');
    const auto = card?.querySelector?.('[data-auto-add-threshold-gift][data-variant-id]');
    const readd = card?.querySelector?.('[data-threshold-gift-readd]');
    const vAttr = auto?.getAttribute('data-variant-id') ?? readd?.getAttribute('data-variant-id');
    if (isThresholdGiftInCart(cart, productId, vAttr)) {
      clearDismissedThresholdGiftPair(String(productId), vAttr);
    }
  }
}

async function fetchCartState() {
  const response = await fetch(Theme.routes.cart_url);
  return response.json();
}

/**
 * Si le cadeau est déjà dans le panier, on efface l’intention de retirer (l’ajout auto peut reprendre).
 * « Réajouter » s’affiche seulement si l’intention de retir existe et que le cadeau n’est plus en ligne.
 */
function setReaddButtonVisible(button, show) {
  if (show) {
    button.classList.add(READD_ACTIVE);
    button.removeAttribute('aria-hidden');
  } else {
    button.classList.remove(READD_ACTIVE);
    button.setAttribute('aria-hidden', 'true');
  }
}

function revealReaddButtons() {
  hydrateDismissedThresholdGiftsFromPersistence();
  return fetchCartState()
    .then((cart) => {
      if (cart?.items?.length) {
        for (const item of cart.items) {
          if (isThresholdGiftLineItem(item) && item.product_id) {
            const vid = item.variant_id ?? item.variantId;
            clearDismissedThresholdGiftPair(String(item.product_id), vid);
          }
        }
      }
      clearRemovedIfGiftLinePresentInDom(cart);
      return cart;
    })
    .then((cart) => {
      for (const button of document.querySelectorAll('[data-threshold-gift-readd]')) {
        const productId = button.getAttribute('data-product-id');
        const vAttr = button.getAttribute('data-variant-id');
        if (!productId) {
          continue;
        }
        if (isThresholdGiftInCart(cart, productId, vAttr)) {
          clearDismissedThresholdGiftPair(productId, vAttr);
          setReaddButtonVisible(button, false);
          continue;
        }
        if (isUserRemovedThresholdGift(productId)) {
          setReaddButtonVisible(button, true);
        } else {
          setReaddButtonVisible(button, false);
        }
      }
    })
    .catch((err) => {
      for (const button of document.querySelectorAll('[data-threshold-gift-readd]')) {
        setReaddButtonVisible(button, false);
      }
      console.error(err);
    });
}

async function removeThresholdGiftLineItem(lineItemKey, cartDrawer) {
  const body = {
    id: lineItemKey,
    quantity: 0,
  };

  if (cartDrawer?.dataset?.sectionId) {
    body.sections = cartDrawer.dataset.sectionId;
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  return response.json();
}

async function addThresholdGiftLineItems(variantIds, cartDrawer) {
  const items = variantIds.map((id) => ({
    id,
    quantity: 1,
    properties: {
      _attribution: 'Rebuy Tiered Progress Bar',
      _threshold_gift: 'true',
    },
  }));

  const body = { items };

  if (cartDrawer?.dataset?.sectionId) {
    body.sections = cartDrawer.dataset.sectionId;
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  return response.json();
}

function synchronizeThresholdGifts() {
  if (window.__cartThresholdGiftSyncPending) {
    return;
  }

  hydrateDismissedThresholdGiftsFromPersistence();

  const eligibleVariantIds = getEligibleThresholdGiftVariantIds();
  const eligibleProductIds = getEligibleThresholdGiftProductIds();
  const cartDrawer = document.querySelector('cart-drawer');

  window.__cartThresholdGiftSyncPending = true;

  fetchCartState()
    .then(async (cartState) => {
      const thresholdGiftItems = cartState.items.filter(isThresholdGiftLineItem);
      const thresholdGiftVariantIds = thresholdGiftItems.map((item) => Number(item.variant_id));
      const existingVariantIdsInCart = cartState.items.map((item) => Number(item.variant_id));

      const lineItemsToRemove = thresholdGiftItems.filter((item) => !eligibleProductIds.has(Number(item.product_id)));
      const variantIdsToAdd = Array.from(eligibleVariantIds).filter(
        (variantId) =>
          !isVariantAutoAddSuppressedByUser(variantId) &&
          !thresholdGiftVariantIds.includes(variantId) &&
          !existingVariantIdsInCart.includes(variantId),
      );

      if (!lineItemsToRemove.length && !variantIdsToAdd.length) {
        return null;
      }

      let parsedResponse = null;

      for (const lineItem of lineItemsToRemove) {
        parsedResponse = await removeThresholdGiftLineItem(lineItem.key, cartDrawer);
        if (parsedResponse?.errors) {
          break;
        }
      }

      if (!parsedResponse?.errors && variantIdsToAdd.length) {
        parsedResponse = await addThresholdGiftLineItems(variantIdsToAdd, cartDrawer);
      }

      return parsedResponse;
    })
    .then((parsed) => {
      if (!parsed) {
        return;
      }

      if (parsed.errors) {
        document.dispatchEvent(
          new CustomEvent('toast:open', { detail: { type: 'error', message: parsed.errors.join?.(', ') || 'Error' } }),
        );
        return;
      }

      if (cartDrawer) {
        const cartIcon = document.querySelector('cart-icon');
        const currentItemCount = cartIcon ? cartIcon.getCurrentItemCount() : 0;
        const itemCount = parsed.item_count ?? currentItemCount;

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount,
              source: 'cart-threshold-gift',
              sections: parsed.sections || {},
            },
          ),
        );
        return;
      }

      window.location.reload();
    })
    .catch(console.error)
    .finally(() => {
      window.__cartThresholdGiftSyncPending = false;
      void revealReaddButtons();
    });
}

init();
